import { browser } from '$app/environment';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ak-theme';

// Browser-chrome colors, kept in sync with the applied mode
const THEME_COLORS = { light: '#F3F7F4', dark: '#03FF93' } as const;

/**
 * Theme store — cycles light → dark → system, defaults to dark
 * (fresh visitors get dark regardless of OS preference).
 * The applied mode is a `light` class on <html> (set pre-paint by the
 * inline script in app.html); everything else re-themes via the token
 * overrides in app.css under `html.light`.
 */
class ThemeStore {
	preference = $state<ThemePreference>('dark');

	constructor() {
		if (browser) {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored === 'light' || stored === 'dark' || stored === 'system') {
				this.preference = stored;
			}
			this.apply();

			// Follow OS changes while in system mode
			window
				.matchMedia('(prefers-color-scheme: light)')
				.addEventListener('change', () => {
					if (this.preference === 'system') this.apply();
				});
		}
	}

	set(preference: ThemePreference) {
		this.preference = preference;
		if (browser) {
			localStorage.setItem(STORAGE_KEY, preference);
			this.apply();
		}
	}

	private apply() {
		if (!browser) return;
		const light =
			this.preference === 'light' ||
			(this.preference === 'system' &&
				window.matchMedia('(prefers-color-scheme: light)').matches);
		document.documentElement.classList.toggle('light', light);
		// Follow the applied mode (not just the OS) for the mobile browser chrome
		document
			.querySelector('meta[name="theme-color"]')
			?.setAttribute('content', light ? THEME_COLORS.light : THEME_COLORS.dark);
	}
}

export const themeStore = new ThemeStore();
